import type { RefObject } from "react";
import { ConfirmDialog } from "@common/ConfirmDialog";
import { ComponentColor } from "@common/types";

interface MarkProcessedDialogProps {
    // Orders that will be sent, and selected orders that will not.
    markableCount: number;
    skippedCount: number;
    // The 1-based position of the order being marked; null before the run.
    progress: number | null;
    // Where focus goes on close when the opener is gone: a run clears the
    // selection, which unmounts the bar that opened the dialog. It must be
    // mounted and enabled in the commit that closes the dialog (the run's
    // results), or focus falls to <body>.
    returnFocusRef: RefObject<HTMLElement | null>;
    onConfirm: () => void;
    onClose: () => void;
}

const orders = (count: number): string =>
    `${count} ${count === 1 ? "order" : "orders"}`;

// Confirms the Orders list's bulk Mark Processed (#473): every move is
// irreversible (Processing goes forward only), so it is asked first, with the
// safe back-out focused (#452). The dialog stays open, counting, while the
// orders are marked one at a time.
export const MarkProcessedDialog = ({
    markableCount,
    skippedCount,
    progress,
    returnFocusRef,
    onConfirm,
    onClose,
}: MarkProcessedDialogProps) => (
    <ConfirmDialog
        title={`Mark ${orders(markableCount)} Processed?`}
        body={
            progress !== null ? (
                <p>
                    Marking {progress} of {markableCount}…
                </p>
            ) : skippedCount > 0 ? (
                <p>
                    {skippedCount} selected{" "}
                    {skippedCount === 1 ? "order" : "orders"} can't be marked
                    Processed and will be skipped.
                </p>
            ) : null
        }
        confirmLabel="Mark Processed"
        confirmColor={ComponentColor.Primary}
        isProcessing={progress !== null}
        returnFocusRef={returnFocusRef}
        onConfirm={onConfirm}
        onClose={onClose}
    />
);
